import {
  COMING_SOON_COPY_LIMITS,
  compactComingSoonCopy,
  defaultComingSoonCopy,
  type StorefrontComingSoonCopy,
  storefrontServingState,
} from "@loot-os/api-contract";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { toast } from "@loot-os/ui/components/sonner";
import { Textarea } from "@loot-os/ui/components/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useStorefrontConfigQuery } from "../lib/storefront";
import { primaryStorefrontHostname, storefrontUrl } from "../lib/storefrontUrl";
import { useTRPC } from "../lib/trpc";
import { useTenantName } from "../lib/useTenantName";
import { useTenantRole } from "../lib/useTenantRole";
import { useUnsavedChangesGuard } from "../lib/useUnsavedChangesGuard";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";

/** The form's fields as strings; a blank field shows the default copy. */
interface Draft {
  headline: string;
  message: string;
  note: string;
}

function toDraft(copy: StorefrontComingSoonCopy): Draft {
  return { headline: copy.headline ?? "", message: copy.message ?? "", note: copy.note ?? "" };
}

function isSame(a: StorefrontComingSoonCopy, b: StorefrontComingSoonCopy): boolean {
  return (
    (a.headline ?? "") === (b.headline ?? "") &&
    (a.message ?? "") === (b.message ?? "") &&
    (a.note ?? "") === (b.note ?? "")
  );
}

/**
 * The Coming soon tab of the Storefront page: the words the coming soon page shows (blank means
 * the default copy), and preview links, which let a teammate see the store as it will be once
 * live while shoppers still see that page. Whether the store is coming soon is the header's
 * control.
 */
export function StorefrontComingSoonEditor() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { can } = useTenantRole();
  const { queryOptions, query } = useStorefrontConfigQuery();
  const tenantName = useTenantName();

  const save = useMutation(
    trpc.storefront.setComingSoonCopy.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        toast.success("Coming soon page saved.");
      },
    }),
  );

  // Narrowing only: the layout renders the page once the same query has data.
  if (!query.data) return null;
  const { config, domains } = query.data;
  const state = storefrontServingState(config);
  const hostname = primaryStorefrontHostname(domains);

  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Coming soon page</CardTitle>
          <CardDescription>
            {state === "coming_soon"
              ? "Every domain of this storefront shows this page, and nothing else, until you set it live in the header."
              : "What every domain of this storefront shows while it is coming soon. Choose Coming soon in the header to show it."}{" "}
            It's drawn in the store's theme, with its logo and social links. Leave a field blank for
            the default words.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ComingSoonCopyForm
            key={JSON.stringify(config.comingSoonCopy)}
            copy={config.comingSoonCopy}
            storeName={config.displayName || tenantName}
            canEdit={can("editor")}
            saving={save.isPending}
            onSave={(copy) => save.mutate({ copy })}
          />
          {hostname && state === "coming_soon" && (
            <p className="mt-4 text-sm">
              <a
                href={storefrontUrl(hostname)}
                target="_blank"
                rel="noreferrer"
                className="text-primary underline-offset-4 hover:underline"
              >
                See the page on {hostname}
              </a>
            </p>
          )}
        </CardContent>
      </Card>
      <PreviewLinkCard hostname={hostname} canEdit={can("editor")} />
    </div>
  );
}

/** A headline, a message and a note; remount with `key` when the saved value changes. */
function ComingSoonCopyForm({
  copy,
  storeName,
  canEdit,
  saving,
  onSave,
}: {
  copy: StorefrontComingSoonCopy;
  storeName: string;
  canEdit: boolean;
  saving: boolean;
  onSave: (copy: StorefrontComingSoonCopy) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(copy));
  const clean = compactComingSoonCopy({
    headline: draft.headline.trim(),
    message: draft.message.trim(),
    note: draft.note.trim(),
  });
  const dirty = !isSame(clean, copy);
  const guard = useUnsavedChangesGuard({ dirty: canEdit && dirty });
  const defaults = defaultComingSoonCopy({ storeName });

  return (
    <form
      className="grid gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(clean);
      }}
    >
      <div className="grid gap-2">
        <Label htmlFor="coming-soon-headline">Headline</Label>
        <Input
          id="coming-soon-headline"
          value={draft.headline}
          maxLength={COMING_SOON_COPY_LIMITS.headline}
          disabled={!canEdit}
          onChange={(e) => setDraft({ ...draft, headline: e.target.value })}
          placeholder={defaults.headline}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="coming-soon-message">Message</Label>
        <Textarea
          id="coming-soon-message"
          value={draft.message}
          maxLength={COMING_SOON_COPY_LIMITS.message}
          disabled={!canEdit}
          rows={3}
          onChange={(e) => setDraft({ ...draft, message: e.target.value })}
          placeholder={defaults.message}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="coming-soon-note">Note</Label>
        <Input
          id="coming-soon-note"
          value={draft.note}
          maxLength={COMING_SOON_COPY_LIMITS.note}
          disabled={!canEdit}
          onChange={(e) => setDraft({ ...draft, note: e.target.value })}
          placeholder="Opening December 2026"
        />
        <p className="text-sm text-muted-foreground">Optional. One short line under the message.</p>
      </div>
      <UnsavedChangesDialog guard={guard} />
      {canEdit && (
        <div>
          <Button type="submit" disabled={saving || !dirty}>
            {saving ? "Saving…" : "Save page"}
          </Button>
        </div>
      )}
    </form>
  );
}

/**
 * Makes a preview link and shows it, with Copy and Open: the link is made by a request, so it is
 * opened by a second click rather than a window opened after the request, which a browser blocks.
 */
function PreviewLinkCard({ hostname, canEdit }: { hostname: string | null; canEdit: boolean }) {
  const trpc = useTRPC();
  const [link, setLink] = useState<{ url: string; expiresAt: string } | null>(null);
  const create = useMutation(
    trpc.storefront.createPreviewLink.mutationOptions({
      onSuccess: (result) => {
        if (!hostname) return;
        const url = new URL("/api/storefront/preview", storefrontUrl(hostname));
        url.searchParams.set("token", result.token);
        setLink({ url: url.toString(), expiresAt: result.expiresAt });
      },
    }),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Preview link</CardTitle>
        <CardDescription>
          Opens the store as it will be once live, on any device, for seven days, while shoppers
          still see the coming soon page. Anyone with the link can preview, so share it only with
          your team.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {!hostname && (
          <p className="text-sm text-muted-foreground">
            This storefront has no active domain yet, so there is nowhere to preview it.
          </p>
        )}
        {link && (
          <div className="grid gap-2">
            <Label htmlFor="preview-link">Link</Label>
            <div className="flex flex-wrap gap-2">
              <Input
                id="preview-link"
                readOnly
                value={link.url}
                className="min-w-0 flex-1 font-mono text-xs"
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  void navigator.clipboard
                    .writeText(link.url)
                    .then(() => toast.success("Preview link copied."))
                }
              >
                Copy
              </Button>
              <a
                href={link.url}
                target="_blank"
                rel="noreferrer"
                className={buttonVariants({ variant: "outline" })}
              >
                Open
              </a>
            </div>
            <p className="text-sm text-muted-foreground">
              Works until {new Date(link.expiresAt).toLocaleString()}.
            </p>
          </div>
        )}
        {canEdit && hostname && (
          <div>
            <Button
              type="button"
              variant={link ? "outline" : "default"}
              disabled={create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending
                ? "Creating…"
                : link
                  ? "Create another link"
                  : "Create preview link"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
